import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { ListSkeleton } from "@/components/common";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Beds from "@/pages/Beds";
import Tenants from "@/pages/Tenants";
import TenantDetail from "@/pages/TenantDetail";
import Rent from "@/pages/Rent";
import More from "@/pages/More";
import MenuPage from "@/pages/MenuPage";
import Announcements from "@/pages/Announcements";
import Complaints from "@/pages/Complaints";
import Settings from "@/pages/Settings";
import AuditLog from "@/pages/AuditLog";
import PublicTenant from "@/pages/PublicTenant";
import PublicShortlist from "@/pages/PublicShortlist";
import JoinRequests from "@/pages/JoinRequests";
import Visits from "@/pages/Visits";
import { ResidentProvider, useResident } from "@/lib/residentAuth";
import ResidentLogin from "@/pages/resident/ResidentLogin";
import ResidentShell from "@/pages/resident/ResidentShell";
import ResidentHome from "@/pages/resident/ResidentHome";
import FindPG from "@/pages/resident/FindPG";
import ListingDetail from "@/pages/resident/ListingDetail";
import ResidentComplaints from "@/pages/resident/ResidentComplaints";
import ResidentProfile from "@/pages/resident/ResidentProfile";
import Saved from "@/pages/resident/Saved";

function ResidentLoginGate() {
  const { resident } = useResident();
  if (resident === undefined) return null;
  return resident ? <Navigate to="/resident" replace /> : <ResidentLogin />;
}

function ResidentArea() {
  return (
    <ResidentProvider>
      <Routes>
        <Route path="login" element={<ResidentLoginGate />} />
        <Route element={<ResidentShell />}>
          <Route index element={<ResidentHome />} />
          <Route path="search" element={<FindPG />} />
          <Route path="pg/:id" element={<ListingDetail />} />
          <Route path="saved" element={<Saved />} />
          <Route path="complaints" element={<ResidentComplaints />} />
          <Route path="profile" element={<ResidentProfile />} />
        </Route>
        <Route path="*" element={<Navigate to="/resident" replace />} />
      </Routes>
    </ResidentProvider>
  );
}

function Splash() {
  return (
    <div className="max-w-md mx-auto p-6 pt-24">
      <ListSkeleton rows={3} />
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { owner, properties } = useAuth();
  const loc = useLocation();
  if (owner === undefined || (owner && properties === null)) return <Splash />;
  if (!owner) return <Navigate to="/login" replace />;
  if (properties.length === 0 && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

function LoginGate() {
  const { owner } = useAuth();
  if (owner === undefined) return <Splash />;
  return owner ? <Navigate to="/" replace /> : <Login />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/t/:token" element={<PublicTenant />} />
            <Route path="/s/:token" element={<PublicShortlist />} />
            <Route path="/resident/*" element={<ResidentArea />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/beds" element={<Beds />} />
              <Route path="/tenants" element={<Tenants />} />
              <Route path="/tenants/:id" element={<TenantDetail />} />
              <Route path="/rent" element={<Rent />} />
              <Route path="/more" element={<More />} />
              <Route path="/more/menu" element={<MenuPage />} />
              <Route path="/more/announcements" element={<Announcements />} />
              <Route path="/more/complaints" element={<Complaints />} />
              <Route path="/more/settings" element={<Settings />} />
              <Route path="/more/audit" element={<AuditLog />} />
              <Route path="/more/join-requests" element={<JoinRequests />} />
              <Route path="/more/visits" element={<Visits />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}
